import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
    EventType,
    useEventBusOn,
    useMenuRouter,
    useWebviewBridge,
    useRouteDataStorage,
    useRouterHistory,
    useChannelContext,
    Channel,
} from 'scbk-services';
import { Dialog, usePageMove, useGlobalPopupContext } from 'scbk-shared-module';

const RouteSubscriber: React.FC = () => {
    const { goMenu } = useMenuRouter();
    const { doNWebBrowser } = useWebviewBridge();
    const { get: getMenuRouteData, remove: removeMenuRouteData } = useRouteDataStorage();
    const { size: historySize } = useRouterHistory();
    const { goMain } = usePageMove();
    const { t } = useTranslation();
    const { channel } = useChannelContext();
    const { openCddQRPop } = useGlobalPopupContext();

    const triggerRef = useRef<HTMLElement | null>(null);

    /**
     * routeData 직렬화
     * localStorage에 실린  값을 사용하고 바로 삭제
     */
    const routeSerialize = (menuId: string) => {
        let json: Record<string, string> = {};

        try {
            json = getMenuRouteData(menuId) ?? {};
            console.log('### MenuRoute menuRouteData ', json);
        } finally {
            removeMenuRouteData(menuId);
        }

        const jsonKeyList = Object.keys(json);
        return jsonKeyList
            .map(function (key) {
                return key + '=' + encodeURIComponent(json[key]);
            })
            .join('&');
    };

    /**
     * 외부 url 열기
     * @param url : url
     * @param jsonSerialize : routeData 직렬화한 string
     */
    const openWebBrowser = (url: string, jsonSerialize: string) => {
        url.indexOf('http') > -1
            ? doNWebBrowser(`${url}${jsonSerialize !== '' ? '?' + jsonSerialize : ''}`)
            : doNWebBrowser(
                  `${location.origin}${url}${jsonSerialize !== '' ? '?' + jsonSerialize : ''}`,
              );
    };

    useEventBusOn({
        eventType: EventType.CDDBlock,
        listener: (args) => {
            console.log('### CDD Block menu id : ', args);
            const showConfirm = async () => {
                triggerRef.current = (document.activeElement as HTMLElement) ?? null;
                const isOk = await Dialog.confirm({
                    title: t('routeSubscriber.cdd-blocking-dialog.001', {
                        defaultValue: '[고객정보확인 안내]',
                    }),
                    content: [
                        `${t('routeSubscriber.cdd-blocking-dialog.002', {
                            defaultValue:
                                '[특정 금융거래정보의 보고 및 이용 등에 관한 법률」 제5조의2(금융회사 등의 고객확인의무)]에 따라 고객확인업무가 필요해요. 모바일 뱅킹 [전체메뉴 > 고객센터 > 고객확인제도] 에서 고객정보를 확인하고 최신내용으로 변경 후 서비스를 이용해 주세요',
                        })}`,
                        `${t('routeSubscriber.cdd-blocking-dialog.003', {
                            defaultValue:
                                '※ 고객정보가 확인되지 않을 경우 은행 일부거래가 제한될 수 있어요.',
                        })}${t('routeSubscriber.cdd-blocking-dialog.004', {
                            defaultValue: '고객정보 변경 후, 다시 이용해 주세요.',
                        })}`,
                    ],
                    cancelBtnLabel: t('routeSubscriber.cdd-blocking-dialog.005', {
                        defaultValue: '취소',
                    }),
                    okBtnLabel: t('routeSubscriber.cdd-blocking-dialog.006', {
                        defaultValue: '고객확인제도',
                    }),
                });

                if (isOk) {
                    // 고객확인 제도로 이동 : shiftMenu로 이동하면 된다.
                    goMenu('MA3CSTCDD001', { historyOptions: { clearHistory: true } });
                } else {
                    triggerRef.current?.focus();
                    historySize() === 0 && goMain();
                }
            };

            if (channel === Channel.IB) {
                openCddQRPop();
            } else {
                showConfirm();
            }
        },
    });

    useEventBusOn({
        eventType: EventType.OpenWebBrowser,
        // eslint-disable-next-line sonarjs/cognitive-complexity
        listener: async (args) => {
            const externalMenuIdArr = [
                'MA3CSTHPL006', // 홈페이지 바로가기 (홈페이지 연결) Shift2차
                'MA3CSTHPL007', // 개인정보처리방침 (홈페이지 연결) Shift2차
                'MA3CSTHPL008', // 신용정보활용체제 (홈페이지 연결) Shift2차
            ];

            // 호출 외부 url
            if (Array.isArray(args) && args[0] && args[1]) {
                const jsonSerialize = routeSerialize(args[0]);

                if (externalMenuIdArr.some((item) => item === args[0])) {
                    triggerRef.current = (document.activeElement as HTMLElement) ?? null;
                    const isOk = await Dialog.confirm({
                        content:
                            args[0] === 'MA3CSTHPL007'
                                ? t('routeSubscriber.open-web-browser-dialog.001', {
                                      defaultValue:
                                          '개인정보처리방침 조회를 위해 SC제일은행 모바일웹 홈페이지로 이동합니다.',
                                  })
                                : args[0] === 'MA3CSTHPL008'
                                  ? t('routeSubscriber.open-web-browser-dialog.003', {
                                        defaultValue:
                                            '신용정보활용체제 조회를 위해 SC제일은행 모바일웹 홈페이지로 이동합니다.',
                                    })
                                  : t('routeSubscriber.open-web-browser-dialog.002', {
                                        defaultValue: 'SC제일은행 모바일웹 홈페이지로 이동합니다.',
                                    }),
                    });
                    if (isOk) {
                        openWebBrowser(args[1], jsonSerialize);
                    } else {
                        triggerRef.current?.focus();
                        historySize() === 0 && goMain();
                    }
                } else {
                    openWebBrowser(args[1], jsonSerialize);
                }
            }
        },
    });

    return null;
};

export { RouteSubscriber };
